import { Phone, Mail, MapPin, Clock, ExternalLink } from "lucide-react";
import {
  PHONE_DISPLAY,
  PHONE_TEL,
  EMAIL,
  GBP_URL,
  SERVICE_AREA,
} from "../../lib/business";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#09090B] py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Left: big CTA card */}
          <div className="md:col-span-7 relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0d1b33] via-zinc-900 to-zinc-900 p-8 sm:p-12">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-[#E10600] mb-3">
              / Get In Touch
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-[0.9]">
              No online booking.
              <br />
              <span className="text-[#E10600]">Just call.</span>
            </h2>
            <p className="mt-5 font-body text-zinc-300 max-w-lg leading-relaxed">
              We keep it old-school on purpose — a quick phone call lets us
              understand your car, your spot, and the right time. It&apos;s
              faster, easier, and you get a real person on the line.
            </p>

            <a
              href={PHONE_TEL}
              data-testid="call-now-contact"
              className="mt-8 inline-flex items-center gap-3 bg-[#E10600] hover:bg-[#FF2D1A] text-white font-display uppercase tracking-wide font-bold text-xl px-8 py-6 rounded-full transition-colors shadow-[0_10px_40px_-10px_rgba(225,6,0,0.6)]"
            >
              <Phone className="w-6 h-6" />
              {PHONE_DISPLAY}
            </a>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 shrink-0">
                  <Mail className="w-4 h-4 text-[#E10600]" />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-xs uppercase tracking-widest text-zinc-500">
                    Email
                  </div>
                  <a
                    href={`mailto:${EMAIL}`}
                    data-testid="email-link"
                    className="font-body text-xs sm:text-sm text-white hover:text-[#E10600] transition-colors break-words"
                  >
                    {EMAIL}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 shrink-0">
                  <MapPin className="w-4 h-4 text-[#E10600]" />
                </div>
                <div>
                  <div className="font-display text-xs uppercase tracking-widest text-zinc-500">
                    Service Area
                  </div>
                  <div className="font-body text-sm text-white">
                    {SERVICE_AREA} & surrounds
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: hours + map */}
          <div className="md:col-span-5 space-y-6 md:space-y-8">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-zinc-900/70 p-8">
              <div className="flex items-center gap-3 mb-5">
                <Clock className="w-5 h-5 text-[#E10600]" />
                <div className="font-display text-xl uppercase font-bold text-white tracking-tight">
                  Hours
                </div>
              </div>
              <div data-testid="hours-block" className="space-y-2">
                <div className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter text-white leading-none">
                  Open <span className="text-[#E10600]">7 days</span>
                </div>
                <div className="font-display text-2xl font-bold uppercase tracking-tight text-zinc-200">
                  7:00 AM – 5:00 PM
                </div>
                <p className="font-body text-sm text-zinc-400 pt-2">
                  Monday through Sunday. Call any time during hours and
                  we&apos;ll get you booked in.
                </p>
              </div>
            </div>

            <a
              href={GBP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gbp-card-link"
              className="block relative overflow-hidden rounded-3xl border border-white/10 group"
            >
              <iframe
                title="Rapid Shine on Google Maps"
                src="https://www.google.com/maps?q=Newcastle+NSW+Australia&output=embed"
                className="w-full h-64 grayscale contrast-125 brightness-75 opacity-90 group-hover:opacity-100 transition-opacity"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center justify-between">
                <div>
                  <div className="font-display text-xs uppercase tracking-widest text-[#E10600]">
                    Google Business
                  </div>
                  <div className="font-display text-lg uppercase font-bold text-white tracking-tight">
                    View on Google
                  </div>
                </div>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#E10600] text-white">
                  <ExternalLink className="w-5 h-5" />
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
